import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { closeSignalModal, openSignalModal, useSignalModal } from "../../lib/signalModalStore.js";
import { SignalDetailBody } from "./SignalDetailBody.js";

/**
 * One shell of the two decision D5 calls for: inspect a signal without losing
 * your place in a hundred-row feed. The other is the route, which renders the
 * same body.
 *
 * A referenced signal swaps in place rather than stacking a second modal, so
 * there is never a pile of dialogs to escape out of one at a time.
 */
export function SignalDetailModal() {
  const { open, signalId } = useSignalModal();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeSignalModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Focus moves in, so the next Tab lands inside the dialog rather than in
    // the feed behind it. The store puts it back on close.
    panelRef.current?.focus();
  }, [open]);

  if (!open || signalId === null) return null;

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: the backdrop is a click target by design; Escape and the close button are the keyboard paths
    // biome-ignore lint/a11y/useKeyWithClickEvents: same — the keyboard path is the document-level Escape handler above
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-surface/80 p-8"
      onClick={closeSignalModal}
      data-testid="signal-modal-backdrop"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: this handler only stops a click reaching the backdrop; it adds no behaviour to reach by keyboard */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Signal detail"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className="flex w-full max-w-3xl flex-col gap-4 border border-border bg-surface-raised p-6"
      >
        <div className="flex items-center justify-between gap-4">
          {/* A plain anchor, matching the feed row: the route is mounted by
              the router owner, and this link is the one path that is meant to
              leave the modal behind entirely. */}
          <a
            href={`/signals/${encodeURIComponent(signalId)}`}
            onClick={closeSignalModal}
            className="text-sm text-accent hover:underline"
          >
            Open the full page
          </a>
          <button
            type="button"
            onClick={closeSignalModal}
            className="border border-border px-3 py-1 text-sm text-text-muted"
          >
            Close
          </button>
        </div>

        <SignalDetailBody signalId={signalId} onOpenSignal={openSignalModal} />
      </div>
    </div>,
    document.body,
  );
}
